import React, {Fragment} from "react";
import {useRouter} from "next/router";
import {IAttributeValue} from "../../types/widgetFiltresTypes";
import {GetServerSideProps, NextPage} from "next";
import {filterAttributeResponse} from "../../helpers/ShopPageCategoryHelper";
import {layoutProps} from "../../services/layoutProps";
import MainLayout, {ICurrencies, ILayout} from "../../components/ForCWV/Wrappers/MainLayout";
import Head from "next/head";
import {domainUrl} from "../../helper";
import CustomCategoryPage, {IProductList} from "../../components/ForCWV/CustomCategoryPage";
import {TranslationsType} from "../../types/homePageTypes";
import {CategoryPageTranslations} from "../../services/translationKeys";
import {
    getBuilderData,
    getCustomerGroups,
    getFilteredCategoryProducts,
    getFiltersCategory,
    getNewCategoryProducts,
} from "../../api/pages/catalog";
import ReCaptchaWrapper from "../../components/shared/ReCaptchaWrapper";

interface ICatalogProps extends ILayout {
    brandList: Array<IAttributeValue>;
    productsList: IProductList,
    dbName: string;
    domain: string;
    titleCategory: string;
    categoryDescription: string | TrustedHTML;
    selectedRate: ICurrencies;
    translationsData: TranslationsType;
    breadcrumb: Array<{ title: string; url: string }>;
    blockStyleHandle: string;
    hasFilterInQuery: boolean;
    metaOptions: Record<string, string>;
    selectedLocale: string;
    backOrderValue: string;
    whichOneRender: {
        firstDivClassName: string;
        firstDivParams: { "data-layout": string } | {};
        secondDivClassName: string;
        showEmptyFiltersSection: boolean;
        showElement: "List" | "Empty"
    };
    getPages: Array<number>;
    getBuilderData: any;
    showBuilderData: boolean,
    currencies: Array<ICurrencies>;
    recaptchaActive: string;
    recaptchaKey: string
}

const Catalog: NextPage<ICatalogProps> = (pageProps) => {
    const {
        dbName,
        domain,
        productsList,
        titleCategory,
        metaOptions: {
            meta_title = "",
            meta_description = "",
            meta_keywords = "",
            image = "",
            tw_title = "",
            tw_desc = "",
            fb_title = "",
            fb_desc = "",
            y_key = ""
        } = {},
        selectedRate,
        selectedLocale: locale,
        brandList,
        categoryDescription,
        translationsData,
        breadcrumb,
        blockStyleHandle,
        hasFilterInQuery,
        whichOneRender,
        backOrderValue,
        getPages,
        getBuilderData,
        showBuilderData,
        currencies,
        isMobile,
        recaptchaActive,
        recaptchaKey
    } = pageProps
    const {asPath} = useRouter();
    const meta_url = asPath.split("?")[0]

    return (
        <ReCaptchaWrapper recaptchaActive={recaptchaActive} recaptchaKey={recaptchaKey} >
            <MainLayout {...pageProps}>
                <Fragment>
                    <Head>
                        <title>{meta_title}</title>
                        <meta name="title" content={meta_title}/>
                        <meta name="description" content={meta_description}/>
                        <meta name="keywords" content={meta_keywords}/>
                        <meta name="generator" content="Zegashop.com"/>
                        <meta name="twitter:title" content={tw_title}/>
                        <meta name="twitter:description" content={tw_desc}/>
                        <meta name="twitter:image" content={domainUrl(`${dbName}/storage/${domain}/${image}`)}/>
                        <meta property="og:title" content={fb_title}/>
                        <meta property="og:description" content={fb_desc}/>
                        <meta property="og:keywords" content={y_key}/>
                        <meta property="og:image" content={domainUrl(`${dbName}/storage/${domain}/${image}`)}/>
                        <meta property="og:url" content={domainUrl(`${dbName}/${locale}${meta_url}`)}/>
                        <meta property="og:locale" content={locale}/>
                        <link rel="canonical" href={domainUrl(`${dbName}/${locale}${meta_url}`)}/>
                    </Head>
                    <CustomCategoryPage
                        brandList={brandList}
                        productsList={productsList}
                        dbName={dbName}
                        titleCategory={titleCategory}
                        categoryDescription={categoryDescription}
                        selectedRate={selectedRate}
                        translationsData={translationsData}
                        breadcrumb={breadcrumb}
                        blockStyleHandle={blockStyleHandle}
                        hasFilterInQuery={hasFilterInQuery}
                        domain={domain}
                        selectedLocale={locale}
                        backOrderValue={backOrderValue}
                        whichOneRender={whichOneRender}
                        getPages={getPages}
                        getBuilderData={getBuilderData}
                        showBuilderData={showBuilderData}
                        currencies={currencies}
                        isMobile={isMobile}
                        recaptchaActive={recaptchaActive}
                    />
                </Fragment>
            </MainLayout>
        </ReCaptchaWrapper>
    )
};
export const getServerSideProps: GetServerSideProps = async ({locale, req, query}) => {
    const {slug, sort_by_price: sortBy, page: currentPage = 1, limit = 12} = query;
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const dbName = req.headers["x-forwarded-host"] as string;
    const models = typedReq.m;
    const domain = typedReq.defaultDB.db_name;
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: CategoryPageTranslations,
        ipv4: typedReq.ipv4
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, isMobile, selectedRate, translationsData: {Home: HomeIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }

    const filterValues: Record<string, string> = Object.keys(query)
        .filter(param => param !== "page")
        .reduce((acc: Record<string, string>, param: string) => {
            const mr = param.match(/^filter_([-_A-Za-z0-9]+)$/);
            if (mr) {
                acc[mr[1]] = query[param] as string;
            }
            return acc;
        }, {});
    let categoryId: number | undefined;
    let categoryDescription: string | undefined;
    let metaOptions: any = {
        meta_title: "",
        meta_description: "",
        meta_keywords: "",
        image: "configuration/logo/logo.webp",
        tw_title: "",
        tw_desc: "",
        fb_title: "",
        fb_desc: "",
        y_key: ""
    };
    let productsList: { data: any[], total: number, pageCount: number } = {data: [], total: 0, pageCount: 1};
    let titleCategory = "";

    function getCategoryDetails(array: any[]) {
        array?.forEach((
            {
                yoast: [{
                    yoast_twitter_title: tw_title = "",
                    yoast_twitter_description: tw_desc = "",
                    yoast_facebook_title: fb_title = "",
                    yoast_facebook_description: fb_desc = "",
                    yoast_focus_keyword: y_key = ""
                } = {}] = [],
                ...e
            }
        ) => {
            if (e?.slug === slug) {
                titleCategory = e?.name
                categoryId = e?.id;
                categoryDescription = e?.description
                metaOptions = {
                    meta_title: !!e?.meta_title ? e?.meta_title : e?.name || "",
                    meta_description: e?.meta_description || "",
                    meta_keywords: e?.meta_keywords || "",
                    image: e?.image ? e?.image : "configuration/logo/logo.webp",
                    tw_title: !!tw_title ? tw_title : !!e?.meta_title ? e?.meta_title : e?.name || "",
                    tw_desc: !!tw_desc ? tw_desc : e?.meta_description || "",
                    fb_title: !!fb_title ? fb_title : !!e?.meta_title ? e?.meta_title : e?.name || "",
                    fb_desc: !!fb_desc ? fb_desc : e?.meta_description || "",
                    y_key: !!y_key ? y_key : e?.meta_keywords || "",
                };
            }
            if (e?.slug == slug && e?.children?.length === 0) {
                categoryId = e?.id;
                return false;
            } else {
                getCategoryDetails(e?.children);
            }
        });
    }

    getCategoryDetails(wrapperProps?.categories?.[0]?.children);
    const {notFound: brandNotFound = 0, ...brands} = await getFiltersCategory(categoryId as number, models, selectedLocale)
    const {id: builderID = 0} = wrapperProps?.categories?.[0]?.children.find(({slug: itemSlug}: {
        slug: string
    }) => itemSlug === slug) || {}
    const getBuilderDataServer = await getBuilderData(builderID + 100, selectedLocale, dbName, domain, selectedRate, models) || []
    const findAllAttributes = await filterAttributeResponse(Object.values(brands), filterValues, models)
    const responseProductList = await getNewCategoryProducts(
        slug as string, selectedLocale, limit, currentPage, selectedRate,
        sortBy === "asc" ? true : sortBy === "desc" ? false : undefined,
        findAllAttributes, true, filterValues, isMobile, models
    );
    responseProductList.emptyCategory = false;

    if (responseProductList?.notFound) {
        responseProductList.data = [];
        responseProductList.emptyCategory = true;
    }
    productsList = await getFilteredCategoryProducts(
        responseProductList, filterValues, currentPage, limit, selectedRate, models
    ) || []
    const customerGroupPrices = await getCustomerGroups(models)

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: titleCategory, url: "/catalog"}
    ];

    const hasFilterInQuery = productsList?.data.length === 0
        && !Object.keys(query).some((lll) => lll.includes("filter"))

    let whichOneRender = {};
    if (productsList.data?.length > 0) {
        whichOneRender = {
            firstDivClassName: "products-list",
            firstDivParams: {"data-layout": "grid-3-sidebar"},
            secondDivClassName: "products-list__body",
            showEmptyFiltersSection: false,
            showElement: "List"
        }
    } else {
        whichOneRender = {
            firstDivClassName: "products-view__empty",
            firstDivParams: {},
            secondDivClassName: "products-view__empty-title",
            showEmptyFiltersSection: true,
            showElement: "Empty"
        }
    }
    const getPages = () => {
        const pages = [];
        const min = Math.max(
            1,
            +currentPage - 2 - Math.max(0, 2 - productsList.pageCount + (+currentPage))
        );
        const max = Math.min(productsList.pageCount, min + 2 * 2);

        for (let i = min; i <= max; i += 1) {
            pages.push(i);
        }
        return pages;
    }

    const brandList = Object.values(JSON.parse(JSON.stringify(brands)))
    const price = brandList.shift()
    brandList.push(price)

    return {
        props: {
            ...wrapperProps,
            productsList,
            brandList,
            titleCategory,
            metaOptions,
            categoryDescription,
            getBuilderData: getBuilderDataServer,
            showBuilderData: Object.values(getBuilderDataServer).some((lll) => (lll as Array<any>)?.length > 0),
            customerGroupPrices: customerGroupPrices ?? [],
            notFound: !!brandNotFound,
            breadcrumb,
            blockStyleHandle: hasFilterInQuery ? "empty_block" : "full_block",
            hasFilterInQuery,
            whichOneRender,
            getPages: getPages(),
            isMobile
        },
    };
}

export default Catalog
